import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { 
  FileText, Merge, Scissors, Settings, Code, Zap, Layers, Move, RotateCcw, Split, Droplet, ListOrdered, Minimize2, Maximize2, Crop, FileSymlink, FileCode, FileImage, QrCode, ScanSearch, Info, BookOpen, ArrowRight, ChevronDown, ChevronUp, Pencil, LayoutGrid
} from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 60 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: "easeOut" }
};

const staggerContainer = {
  initial: "initial",
  whileInView: "animate",
  viewport: { once: true },
  variants: {
    animate: {
      transition: {
        staggerChildren: 0.07
      }
    }
  }
};

const allFeatureCategories = [
  {
    name: "Manipulate PDFs",
    value: "manipulate",
    features: [
      { icon: Merge, title: "Merge PDFs", desc: "Combine multiple PDF files into a single document.", codeExample: "POST /v1/pdf/merge\n{\n  \"files\": [\"file_id_1\", \"file_id_2\"]\n}", apiDocLink: "/docs/merge-pdfs", badgeText: "8 Execution Modes" },
      { icon: Scissors, title: "Extract Pages", desc: "Select and extract specific pages from a PDF.", codeExample: "POST /v1/pdf/extract\n{\n  \"file_id\": \"file_id_1\",\n  \"pages\": \"1,3-5\"\n}", apiDocLink: "/docs/extract-pages", badgeText: "5 Output Variants" },
      { icon: Layers, title: "Delete Pages", desc: "Remove unwanted pages from your PDF documents.", codeExample: "POST /v1/pdf/delete-pages\n{\n  \"file_id\": \"file_id_1\",\n  \"pages_to_delete\": \"2,4\"\n}", apiDocLink: "/docs/delete-pages", badgeText: "3 Options" },
      { icon: Move, title: "Reorder Pages", desc: "Change the order of pages within a PDF file.", codeExample: "POST /v1/pdf/reorder\n{\n  \"file_id\": \"file_id_1\",\n  \"new_order\": [3,1,2]\n}", apiDocLink: "/docs/reorder-pages", badgeText: "3 Modes" },
      { icon: RotateCcw, title: "Rotate Pages", desc: "Rotate pages in a PDF to the correct orientation.", codeExample: "POST /v1/pdf/rotate\n{\n  \"file_id\": \"file_id_1\",\n  \"angle\": 90\n}", apiDocLink: "/docs/rotate-pages", badgeText: "4 Rotation Variants" },
      { icon: Split, title: "Split PDF", desc: "Divide a large PDF into multiple smaller files.", codeExample: "POST /v1/pdf/split\n{\n  \"file_id\": \"file_id_1\",\n  \"split_after_pages\": [2,5]\n}", apiDocLink: "/docs/split-pdf", badgeText: "6 Split Types" },
    ]
  },
  {
    name: "Enhance Documents",
    value: "enhance",
    features: [
      { icon: Droplet, title: "Add Watermarks", desc: "Protect or brand documents with text or image watermarks.", codeExample: "POST /v1/pdf/watermark\n{\n  \"file_id\": \"file_id_1\",\n  \"text\": \"CONFIDENTIAL\"\n}", apiDocLink: "/docs/add-watermarks", badgeText: "6 Watermark Modes" },
      { icon: ListOrdered, title: "Add Page Numbers", desc: "Easily insert page numbers with custom formatting.", codeExample: "POST /v1/pdf/page-numbers\n{\n  \"file_id\": \"file_id_1\",\n  \"position\": \"bottom-center\"\n}", apiDocLink: "/docs/add-page-numbers", badgeText: "5 Numbering Variants" },
      { icon: Minimize2, title: "Flatten PDF", desc: "Make annotations and form fields non-editable.", codeExample: "POST /v1/pdf/flatten\n{\n  \"file_id\": \"file_id_1\"\n}", apiDocLink: "/docs/flatten-pdf", badgeText: "2 Flattening Options" },
      { icon: Maximize2, title: "Resize PDF", desc: "Change the page dimensions of your PDF documents.", codeExample: "POST /v1/pdf/resize\n{\n  \"file_id\": \"file_id_1\",\n  \"size\": \"A4\"\n}", apiDocLink: "/docs/resize-pdf", badgeText: "4 Resize Profiles" },
      { icon: Zap, title: "Optimize PDF", desc: "Reduce file size while maintaining quality for web.", codeExample: "POST /v1/pdf/optimize\n{\n  \"file_id\": \"file_id_1\",\n  \"level\": \"web\"\n}", apiDocLink: "/docs/optimize-pdf", badgeText: "3 Compression Modes" },
      { icon: Crop, title: "Crop PDF", desc: "Adjust page margins or remove unwanted areas.", codeExample: "POST /v1/pdf/crop\n{\n  \"file_id\": \"file_id_1\",\n  \"crop_box\": [10,10,100,100]\n}", apiDocLink: "/docs/crop-pdf", badgeText: "2 Crop Styles" },
    ]
  },
  {
    name: "Convert Formats",
    value: "convert",
    features: [
      { icon: FileSymlink, title: "DOCX to PDF", desc: "Convert Word documents to professional PDFs.", codeExample: "POST /v1/convert/docx-to-pdf\n{\n  \"file_id\": \"docx_file_id\"\n}", apiDocLink: "/docs/docx-to-pdf", badgeText: "Standard Conversion" },
      { icon: FileSymlink, title: "XLSX to PDF", desc: "Transform Excel spreadsheets into PDF format.", codeExample: "POST /v1/convert/xlsx-to-pdf\n{\n  \"file_id\": \"xlsx_file_id\"\n}", apiDocLink: "/docs/xlsx-to-pdf", badgeText: "Spreadsheet Presets" },
      { icon: FileSymlink, title: "PPTX to PDF", desc: "Convert PowerPoint presentations to shareable PDFs.", codeExample: "POST /v1/convert/pptx-to-pdf\n{\n  \"file_id\": \"pptx_file_id\"\n}", apiDocLink: "/docs/pptx-to-pdf", badgeText: "Presentation Styles" },
      { icon: FileCode, title: "PDF to Word", desc: "Convert PDFs back to editable Word documents.", codeExample: "POST /v1/convert/pdf-to-docx\n{\n  \"file_id\": \"pdf_file_id\"\n}", apiDocLink: "/docs/pdf-to-word", badgeText: "Editable + Styled" },
      { icon: FileCode, title: "PDF to HTML", desc: "Transform PDFs into web-friendly HTML files.", codeExample: "POST /v1/convert/pdf-to-html\n{\n  \"file_id\": \"pdf_file_id\"\n}", apiDocLink: "/docs/pdf-to-html", badgeText: "1 HTML Package" },
      { icon: FileImage, title: "PDF to Image", desc: "Convert PDF pages to JPG, PNG, or other image formats.", codeExample: "POST /v1/convert/pdf-to-image\n{\n  \"file_id\": \"pdf_file_id\",\n  \"format\": \"png\"\n}", apiDocLink: "/docs/pdf-to-image", badgeText: "2 Output Types" },
    ]
  },
  {
    name: "Advanced Tools",
    value: "tools",
    features: [
      { icon: QrCode, title: "Generate QR Code", desc: "Create QR codes for URLs, text, contact info, and more.", codeExample: "POST /v1/qr/generate\n{\n  \"data\": \"https://webfiny.com\"\n}", apiDocLink: "/docs/generate-qr", badgeText: "4 QR Styles" },
      { icon: ScanSearch, title: "Smart QR Code", desc: "Generate dynamic QR codes with tracking and analytics.", codeExample: "POST /v1/qr/smart\n{\n  \"data\": \"https://webfiny.com\",\n  \"tracking_enabled\": true\n}", apiDocLink: "/docs/smart-qr", badgeText: "6 QR Data Types" },
      { icon: Info, title: "Add Metadata", desc: "Embed or update metadata like author, title, keywords.", codeExample: "POST /v1/pdf/metadata/add\n{\n  \"file_id\": \"file_id_1\",\n  \"title\": \"My Document\"\n}", apiDocLink: "/docs/add-metadata", badgeText: "3 Metadata Options" },
      { icon: BookOpen, title: "Read Metadata", desc: "Extract and view existing metadata from PDF files.", codeExample: "GET /v1/pdf/metadata/read?file_id=file_id_1", apiDocLink: "/docs/read-metadata", badgeText: "2 Modes" },
      { icon: Settings, title: "Custom Properties", desc: "Add or modify custom document properties.", codeExample: "POST /v1/pdf/properties/custom\n{\n  \"file_id\": \"file_id_1\",\n  \"custom_prop\": \"value\"\n}", apiDocLink: "/docs/custom-properties", badgeText: "2 Structure Types" },
      { icon: FileText, title: "Text Extraction", desc: "Extract all text content from PDF documents.", codeExample: "GET /v1/pdf/text?file_id=file_id_1", apiDocLink: "/docs/text-extraction", badgeText: "2 Output Formats" },
      { icon: Pencil, title: "Edit PDFs", desc: "Modify, annotate, and adjust PDF content visually.", codeExample: "POST /v1/pdf/edit\n{\n  \"file_id\": \"file_id_1\",\n  \"changes\": [...]\n}", apiDocLink: "/solutions/pdf-editor", isSolutionLink: true, badgeText: "WYSIWYG + API Modes" },
      { icon: LayoutGrid, title: "Use Templates", desc: "Design once, reuse forever. Generate PDFs from dynamic data.", codeExample: "POST /v1/pdf/generate-from-template\n{\n  \"template_id\": \"tpl_abc\",\n  \"data\": {...}\n}", apiDocLink: "/solutions/templates", isSolutionLink: true, badgeText: "Designer + JSON" },
    ]
  }
];

const FeaturesSection = ({ onActionClick }) => {
  const [showAllFeatures, setShowAllFeatures] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const toggleShowAllFeatures = () => {
    setShowAllFeatures(!showAllFeatures);
    if (!showAllFeatures) {
      onActionClick('View All Features (expanded)');
    }
  };

  const handleFeatureClick = (feature) => {
    if (feature.isSolutionLink) {
      onActionClick(`Learn more about ${feature.title}`);
    } else {
      setSelectedFeature(feature);
      setIsModalOpen(true);
    }
  };

  const handleDocsLink = (link) => {
    setIsModalOpen(false);
    onActionClick(`View API Docs for ${selectedFeature?.title} (link: ${link})`);
  };
  
  const renderFeatureCard = (feature, index, isAccordionView = false) => (
    <motion.div
      key={index}
      className={`bg-card p-6 rounded-xl shadow-xl hover-lift border border-border flex flex-col cursor-pointer ${isAccordionView ? 'bg-background' : ''} relative`}
      variants={fadeInUp}
      onClick={() => handleFeatureClick(feature)}
      role="button"
      tabIndex={0}
      onKeyPress={(e) => e.key === 'Enter' && handleFeatureClick(feature)}
    >
      {feature.badgeText && (
        <div className="absolute top-3 right-3 bg-primary text-primary-foreground text-xs font-bold px-2 py-1 rounded-full shadow-md">
          {feature.badgeText}
        </div>
      )}
      <div className={`bg-primary/10 w-12 h-12 rounded-lg flex items-center justify-center mb-5 ${isAccordionView ? 'mr-4' : ''}`}>
        <feature.icon className="h-6 w-6 text-primary" />
      </div>
      <div className={isAccordionView ? 'flex-grow' : ''}>
        <h3 className={`text-xl font-bold mb-2 text-card-foreground font-heading ${isAccordionView ? 'text-md' : ''}`}>{feature.title}</h3>
        <p className={`text-foreground text-sm ${isAccordionView ? '' : 'flex-grow'}`}>{feature.desc}</p>
      </div>
      <div className={`mt-4 text-primary text-xs font-semibold flex items-center ${isAccordionView ? 'mt-2' : ''}`}>
        Learn More <ArrowRight className="ml-1 h-3 w-3" />
      </div>
    </motion.div>
  );


  return (
    <section id="features" className="py-20 bg-background border-t border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          className="text-center mb-12"
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={fadeInUp}
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4 font-heading text-primary-foreground">
            What You Can <span className="gradient-text-brand">Do</span>
          </h2>
          <p className="text-xl text-foreground max-w-3xl mx-auto">
            Over 20 powerful PDF API features at your fingertips, neatly organized. Click a feature to learn more.
          </p>
        </motion.div>

        <Tabs defaultValue="manipulate" className="w-full">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <TabsList className="grid w-full grid-cols-2 md:grid-cols-4 gap-2 mb-10 bg-card border border-border">
              {allFeatureCategories.map((category) => (
                <TabsTrigger key={category.value} value={category.value} className="text-xs sm:text-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                  {category.name}
                </TabsTrigger>
              ))}
            </TabsList>
          </motion.div>

          {allFeatureCategories.map((category) => (
            <TabsContent key={category.value} value={category.value}>
              <motion.div 
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
                {...staggerContainer}
              >
                {category.features.map((feature, index) => renderFeatureCard(feature, index))}
              </motion.div>
            </TabsContent>
          ))}
        </Tabs>
        
        <motion.div 
          className="text-center mt-16"
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={fadeInUp}
        >
          <Button 
            onClick={toggleShowAllFeatures}
            size="lg" 
            className="bg-primary text-primary-foreground hover:bg-primary/80 px-8 py-3 text-lg pulse-glow-brand shadow-lg"
            aria-expanded={showAllFeatures}
          >
            {showAllFeatures ? 'Hide All Features' : 'View All Features'} 
            {showAllFeatures ? <ChevronUp className="ml-2 h-5 w-5" /> : <ChevronDown className="ml-2 h-5 w-5" />}
          </Button>
        </motion.div>

        <AnimatePresence>
          {showAllFeatures && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.5, ease: "easeInOut" }}
              className="mt-12 overflow-hidden"
            >
              <Accordion type="multiple" className="w-full bg-card rounded-xl p-4 border border-border shadow-xl">
                {allFeatureCategories.map((category) => (
                  <AccordionItem value={category.value} key={category.value}>
                    <AccordionTrigger className="text-xl font-heading hover:text-primary">
                      {category.name}
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pt-2">
                        {category.features.map((feature, index) => renderFeatureCard(feature, index, true))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {isModalOpen && selectedFeature && (
          <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
            <DialogContent className="sm:max-w-2xl bg-card border-border text-card-foreground">
              <DialogHeader>
                <DialogTitle className="text-2xl font-heading flex items-center">
                  <selectedFeature.icon className="h-7 w-7 text-primary mr-3" />
                  {selectedFeature.title}
                </DialogTitle>
                <DialogDescription className="text-muted-foreground pt-2">
                  {selectedFeature.desc}
                </DialogDescription>
              </DialogHeader>
              
              <div className="my-6">
                <h4 className="text-lg font-semibold mb-2 text-primary-foreground font-heading">Example API Call:</h4>
                <pre className="code-block text-sm overflow-x-auto">
                  <code>{selectedFeature.codeExample}</code>
                </pre>
              </div>

              <DialogFooter className="sm:justify-start">
                <Button 
                  type="button" 
                  variant="outline"
                  className="border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                  onClick={() => handleDocsLink(selectedFeature.apiDocLink)}
                >
                  <BookOpen className="mr-2 h-4 w-4" /> View API Docs
                </Button>
                <DialogClose asChild>
                  <Button type="button" variant="secondary" className="bg-muted text-muted-foreground hover:bg-muted/80">
                    Close
                  </Button>
                </DialogClose>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </AnimatePresence>
    </section>
  );
};

export default FeaturesSection;